import * as apiValues from '../constants/apiValues';
import { EMAIL_FIELD_HINT, EMAIL_REGISTER_OPTIONS, ZIP_FIELD_HINT, ZIP_REGISTER_OPTIONS } from '../constants/formValidation';
import * as helpers from '../helpers';
import * as pageGetInvolvedFeedbackActions from '../actions/pageGetInvolvedFeedbackActions';
import DocumentTitle from './DocumentTitle';
import MBreadcrumbs from './MBreadcrumbs';
import PageBase from './PageBase';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import SuperLink from './SuperLink';
import { FormGroup, Input, Label } from 'reactstrap';
import ValidatedFormField from './ValidatedFormField';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from '../withRouterCompat';
import Select from './Select';
import {
  PARTNER_WITH_LADOT_INTRO,
  PARTNER_WITH_LADOT_TITLE,
  isPartnerGetInvolvedSearch,
} from '../utils/getInvolvedPartnerForm';
import { MASTER_AUDIENCE_LIST_ID, resolveMailingListTags } from '../helpers/mailchimpTags';

const iWantToOptions = [
  {
    id: 'become-a-leader',
    title: 'Become a Leader',
    copy: 'Participate in leadership development training, identify the best ways to conduct engagement, advocate for street improvements, and help shape projects in your community.',
  },
  {
    id: 'ladot-your-event',
    title: 'Request LADOT at Your Event',
    copy: 'Our staff is happy to bring information and opportunities for involvement to your community event. Typical events include community fairs, Neighborhood Council meetings, town halls, and other similar public meetings and events. Note: LADOT branded materials will not always be available.',
  },
  {
    id: 'become-a-partner',
    title: 'Become a Partner Organization',
    copy: 'Participate in leadership development training, identify the best ways to conduct engagement, advocate for street improvements, and help shape projects in the communities where your organization works.',
  },
  { title: 'Other', copy: 'Share your questions, concerns, and ideas below.' },
];

function PageGetInvolvedFeedback(props) {
  const isPartnerForm = isPartnerGetInvolvedSearch(props.location?.search);
  const [iWantTo, setIWantTo] = useState(isPartnerForm ? PARTNER_WITH_LADOT_TITLE : 'Share My Story');
  const [sendTo, setSendTo] = useState();
  const [formError, setFormError] = useState('');
  const timeout1Ref = useRef(null);
  const timeout2Ref = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      nameFirst: '',
      nameLast: '',
      zip: '',
      message: '',
      joinMailingList: false,
    },
  });
  const { ref: joinMailingListRef, ...joinMailingListField } = register('joinMailingList');

  function fetchContent() {
    props.actions.initPageGetInvolvedFeedback();
    const search = props.location?.search || '';
    if (isPartnerGetInvolvedSearch(search)) {
      setIWantTo(PARTNER_WITH_LADOT_TITLE);
      return;
    }
    const iWantToMatch = iWantToOptions.find(o => o.id == helpers.parseUrlSearch(search).iWantTo);
    setIWantTo(iWantToMatch ? iWantToMatch.title : 'Share My Story');
  }

  useEffect(() => {
    fetchContent();
  }, [props.location.pathname, props.location.search]);

  function iWantTo_onChange(value) {
    clearTimeout(timeout1Ref.current);
    timeout1Ref.current = setTimeout(() => {
      setIWantTo(value);
    }, 50);
  }

  function sendTo_onChange(value) {
    clearTimeout(timeout2Ref.current);
    timeout2Ref.current = setTimeout(() => {
      setSendTo(value);
    }, 50);
  }

  function onSubmit(values) {
    const programs = props.page.programs || [];

    setFormError('');
    const selectedProgram = programs.find(program => program.id == sendTo || program.documentId == sendTo);
    const joinList =
      typeof values.joinMailingList === 'boolean'
        ? values.joinMailingList
        : Array.isArray(values.joinMailingList)
          ? values.joinMailingList.length > 0
          : !!values.joinMailingList;

    const payload = {
      ...values,
      sendTo: selectedProgram
        ? selectedProgram.documentId || selectedProgram.id || selectedProgram.slug
        : null,
      joinMailingList: joinList,
      iWantTo: isPartnerForm ? PARTNER_WITH_LADOT_TITLE : iWantTo,
      listId: MASTER_AUDIENCE_LIST_ID,
      tags: resolveMailingListTags({
        mailchimpTag: selectedProgram && selectedProgram.mailchimpTag,
      }),
      ...(isPartnerForm ? { formVariant: 'partner' } : {}),
    };

    fetch(`${apiValues.getCmsPath()}/api/getinvolved`, {
      method: 'post',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(
      result => {
        result.json().then(
          body => {
            if (body && body.delivery) {
              console.info(`getinvolved delivery\n${JSON.stringify(body.delivery, null, 2)}`);
            }
            if (result.status == 200) {
              window.location = '/get-involved/thanks';
              return;
            }
            handleFailure(result, body);
          },
          () => handleFailure(result),
        );
      },
      result => handleFailure(result),
    );
  }

  function handleFailure(result, body) {
    console.error('handleValidSubmitFailure', result.status, body || result);
    const detail = body && (body.message || (body.delivery && body.delivery.staff && body.delivery.staff.message));
    setFormError(detail || 'There was a problem submitting your feedback. Please try again later.');
  }

  function renderBreadcrumbs() {
    const pageTitle = isPartnerForm ? PARTNER_WITH_LADOT_TITLE : 'We Want To Hear From You!';
    const mBreadcrumbs = {
      pageChain: [
        { browserTitle: 'Home', slug: '' },
        { browserTitle: 'Get Involved', slug: '#' },
        { browserTitle: pageTitle, slug: '#' },
      ],
    };

    return <MBreadcrumbs className="coupling coupling--full-centered" mBreadcrumbs={mBreadcrumbs} />;
  }

  function renderForm() {
    const programs = props.page.programs || [];

    const iWantToOptionItems = iWantToOptions.map(option => {
      return { title: option.title, value: option.title };
    });
    const q2OptionItems = [
      { disabled: true, title: 'Select a program (optional)', value: '' },
      ...programs.map(program => {
        return { title: program.title, value: program.id };
      }),
    ];
    const sendToClass = !sendTo || sendTo == '' ? 'select--is-unset' : '';

    return (
      <div className="page-getinvolvedfeedback__form">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {isPartnerForm ? null : (
            <div className="select-container">
              <label htmlFor="getInvolvedSelect1">I Want To</label>
              <Select
                defaultValue={iWantTo}
                id="getInvolvedSelect1"
                onSelectedChange={v => iWantTo_onChange(v)}
                options={iWantToOptionItems}
                value={iWantTo}
              />
            </div>
          )}
          {isPartnerForm ? null : renderOptionCopy()}
          <div className="select-container">
            <label htmlFor="getInvolvedSelect2">Send To</label>
            <Select
              className={sendToClass}
              defaultValue={''}
              id="getInvolvedSelect2"
              onSelectedChange={v => sendTo_onChange(v)}
              options={q2OptionItems}
              value={sendTo}
            />
          </div>
          <ValidatedFormField
            id="gif-email"
            name="email"
            label="Email Address"
            type="email"
            required
            hint={EMAIL_FIELD_HINT}
            error={errors.email}
            register={register}
            registerOptions={EMAIL_REGISTER_OPTIONS}
          />
          <ValidatedFormField
            id="gif-first"
            name="nameFirst"
            label="Your First Name"
            type="text"
            required
            error={errors.nameFirst}
            register={register}
            registerOptions={{ required: 'First name is required' }}
          />
          <ValidatedFormField
            id="gif-last"
            name="nameLast"
            label="Your Last Name"
            type="text"
            required
            error={errors.nameLast}
            register={register}
            registerOptions={{ required: 'Last name is required' }}
          />
          <ValidatedFormField
            id="gif-zip"
            name="zip"
            label="Your Zip Code"
            type="text"
            inputMode="numeric"
            maxLength={5}
            required
            hint={ZIP_FIELD_HINT}
            error={errors.zip}
            register={register}
            registerOptions={ZIP_REGISTER_OPTIONS}
          />
          <ValidatedFormField
            id="gif-message"
            name="message"
            label="Message"
            type="textarea"
            required
            error={errors.message}
            register={register}
            registerOptions={{ required: 'Message is required' }}
          />
          <FormGroup check>
            <Label check>
              <Input type="checkbox" innerRef={joinMailingListRef} {...joinMailingListField} /> Join mailing list (be informed by email about our latest news and events).
            </Label>
          </FormGroup>
          <p>
            Your contact information is used to deliver information and updates.
            <br />
            <SuperLink to="/content/privacy-policy">Privacy Policy</SuperLink>
          </p>
          {formError ? (
            <p className="form-error" role="alert">
              {formError}
            </p>
          ) : null}
          <button type="submit" className="tg-button tg-button--pill tg-button--black">
            <span className="tg-button__label">Send Message</span>
          </button>
        </form>
      </div>
    );
  }

  function renderOptionCopy() {
    if (!iWantTo) return null;

    const selectedOption = iWantToOptions.find(option => option.title == iWantTo);
    if (!selectedOption) return null;

    return <div className="page-getinvolvedfeedback__i-want-to-copy">{selectedOption.copy}</div>;
  }

  function renderIntro() {
    return (
      <div className="page-getinvolvedfeedback__intro">
        <div className="page-getinvolvedfeedback__intro-title">
          {isPartnerForm ? PARTNER_WITH_LADOT_TITLE : 'We Want To Hear From You!'}
        </div>
        <div className="page-getinvolvedfeedback__intro-subtext">
          {isPartnerForm
            ? PARTNER_WITH_LADOT_INTRO
            : 'Use the form below to share your story or innovative idea, or become an LADOT partner organization or leader in your community.'}
        </div>
      </div>
    );
  }

  return (
    <PageBase className="page-getinvolvedfeedback">
      <DocumentTitle title={apiValues.buildTitle(isPartnerForm ? PARTNER_WITH_LADOT_TITLE : 'We Want To Hear From You!')}/>
      {renderBreadcrumbs()}
      <div className="coupling coupling--centered-thin">
        <div className="container container--coupling">
          {renderIntro()}
          {renderForm()}
        </div>
      </div>
    </PageBase>
  );
}

export default withRouter(
  connect(
    state => ({
      page: state.page,
    }),
    dispatch => ({
      actions: bindActionCreators(pageGetInvolvedFeedbackActions, dispatch),
    }),
  )(PageGetInvolvedFeedback),
);

PageGetInvolvedFeedback.propTypes = {
  actions: PropTypes.object,
  className: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object,
  page: PropTypes.object,
};
